import { Elements, TextButton } from '@tfo/common'
import { inject, observer } from 'mobx-react'
import { FormattedMessage, injectIntl, useIntl, WrappedComponentProps } from 'react-intl'
import { RouteComponentProps, withRouter } from 'react-router'
import { useState } from 'react'
import Loader from '../../components/common/loader/Loader'
import RootStore from '../../stores/RootStore'
import VerificationCodeLogin from './VerificationCodeLogin'
import LanguageSelector from '../../components/header/LanguageSelector'
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined'
import bank from '../../media/images/BankID_logo.svg'
import './LoginIndividual.scss'
import { BankId } from '@tfo/ppl'
import $http from "../../utils/$http"
import { LoginType } from '@tfo/ppl'


export interface ILoginIndividual {
	rootStore?: RootStore
}

const { Heading3 } = Elements

const LoginIndividual = (
	props: ILoginIndividual & WrappedComponentProps & RouteComponentProps,
) => {
	const intl = useIntl()
	const store = props.rootStore!.loginStore!
	const userStore = props.rootStore.userStore
	const loginStore = props.rootStore.loginStore

	const bankIdIcon = () => <img className="bankId-icon" src={bank} alt="BankId Logo"></img>
	const [loginType, setLoginType] = useState<LoginType>(loginStore.selectedLoginType === LoginType.VerificationCode ? LoginType.VerificationCode : LoginType.None)


	const hrefQuery = new URLSearchParams(document.location.search)
	const hideCancelButton: boolean = hrefQuery.get("loginWith") === "verificationCode" ? true : false

	const login = async (encryptedCompletionData: string) => {
		store.onBankIdLogin(encryptedCompletionData)
	}

	const onSetLoginType = (loginType: LoginType) => {
		setLoginType(loginType)
	}

	const qrCodeBankId = async (protectedQrState: string): Promise<{ qrCode: string }> => {
		return await $http.post<{ qrCode: string }>('authentication/bankid/qrcode', {
			protectedQrState: protectedQrState
		})
	}

	const authBankId = async (onSameDevice: boolean): Promise<{
		protectedOrderRef: string
		protectedQrStartState: string
		qrCode: string
		launchUrl: string
	}> => {
		return await $http.post<{
			protectedOrderRef: string
			protectedQrStartState: string
			qrCode: string
			launchUrl: string
		}>('authentication/bankid/auth', {
			isSameDevice: onSameDevice
		})
	}

	const collectBankId = async (protectedOrderRef, attempt): Promise<{
		completionData?: string;
		statusMessage?: string,
		hintCode?: string
	}> => {
		return await $http.post<{
			completionData?: string;
			statusMessage?: string,
			hintCode?: string
		}>
			('authentication/bankid/collect', {
				protectedOrderRef,
				attempt
			});
	}


	const cancelBankIdSession = async (orderRef: string) => {
		await $http.post<{
			protectedOrderRef: string
			protectedQrStartState: string
			qrCode: string
		}>('authentication/bankid/cancel', {
			protectedOrderRef: orderRef
		})
	}

	return (
		<div className="LoginIndividual">
			<div className="LoginIndividualHeader">
				<Heading3 style={{ color: '#009B48' }}>
					<FormattedMessage id="common.login" />
				</Heading3>
				<LanguageSelector
					language={userStore.languageSettings.language}
					onSelectLanguageCode={l => userStore.setLanguage(l)}
					displayAs="Switch" />
			</div>

			{loginType === LoginType.None &&
				<>
					<TextButton
						onClick={() => setLoginType(LoginType.BankId)}
						className="auth-button"
						secondary
						icon={bankIdIcon()}>{intl.formatMessage({ id: 'bankid.startButton' })}
					</TextButton>

					<TextButton
						onClick={() => setLoginType(LoginType.VerificationCode)}
						className="auth-button"
						icon={<VerifiedUserOutlinedIcon className='verfication-code-icon' />}
						secondary>{intl.formatMessage({ id: 'common.verificationCode' })}
					</TextButton>
				</>
			}

			{loginType === LoginType.VerificationCode &&
				<VerificationCodeLogin
					onProgress={loading => store.loading = loading}
					onReset={onSetLoginType}
					hideCancelButton={hideCancelButton} />
			}

			{loginType === LoginType.BankId &&
				<BankId
					authenticateEncryptedData={login}
					startAuthOnDisplay={false}
					cancelBankIdSession={cancelBankIdSession}
					collectBankId={collectBankId}
					authBankId={authBankId}
					onReset={onSetLoginType}
					qrCodeBankId={qrCodeBankId} />
			}

			<div className="LoginInfo">
				<h6 className='LoginInfo-header'>
					<FormattedMessage id="login.information.title" />
				</h6>
				<p className='LoginInfo-paragraph'>
					<FormattedMessage id="login.information.contact" />
					<a
						className='info-mail-link'
						href={'mailto:info@tofindout.se'}
						target='_self'>
						{intl.formatMessage({ id: 'login.information.mail' })}
					</a>
				</p>
			</div>

			{store.loading && (
				<Loader
					startingBankID={store.openBankID}
					selectedLoginType={store.selectedLoginType}
					statusText={store.bankIDStatusText}
				/>
			)}
		</div>
	)
}

export default withRouter(
	inject('rootStore')(injectIntl(observer(LoginIndividual))),
)
